import { network } from "web-utils-network";
import localConfig from "./localConfig";
import baseConfig from "./baseConfig";
import deepMerge from "../utils/deepMerge";

let appConfig = null;
const CONFIG_RETRY_PERIOD = 2000;

function tryGettingConfig(resolve) {
  if (appConfig) {
    resolve(appConfig);
  } else {
    setTimeout(() => tryGettingConfig(resolve), CONFIG_RETRY_PERIOD);
  }
}

export const config = {
  /**
   * If there is a local config file already set, it just returns it synchronously. If there is no config file set yet
   * it will perform a network request to the remote server to gather one and return it asynchronously. If the network
   * call fails the base config file will be returned as a fallback by the promise.
   * @returns {Object|Promise<Object>}  A configuration object if there is already one set in memory or a promise that
   * will resolve to a configuration object once it is received from the remote location.
   */
  async request() {
    if (appConfig) {
      return appConfig;
    }
    try {
      const response = await network.get(`${localConfig.configUrl}/${localConfig.appName}`);
      appConfig = deepMerge(baseConfig, response.data);
      return appConfig;
    } catch (e) {
      appConfig = baseConfig;
      return appConfig;
    }
  },
  /**
   * It returns the application configuration object if there is one in memory. In case of not having one, it will wait
   * for an amount of seconds to retry to get the object from memory. This method should be used as a getter and always
   * after the request method has been called before, given that this method only checks the local configuration file
   * periodically.
   * @returns {Object|Promise} The app config if it's ready in memory, or a Promise that will resolve when the config
   * file arrives to the app.
   */
  get() {
    return appConfig || new Promise(resolve => tryGettingConfig(resolve));
  }
};
